import { useEffect, useLayoutEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { cn } from '@/lib/utils';

// Explicit pin icon (avoids relying on `L.Icon.Default` under Vite).
const SITE_PIN_ICON = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export type SitePlacement = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
};

type PaneConfig = {
  normal: string;
  behindOverlays: string;
};

function dotIcon(selected: boolean) {
  return L.divIcon({
    className: cn('!border-0 !bg-transparent'),
    html: `<div style="width:16px;height:16px;border-radius:9999px;background:${
      selected ? '#0ea5e9' : '#64748b'
    };border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

export function SitePlacementLayer({
  mode,
  markerStyle = 'dot',
  sites,
  selectedSiteId,
  onSelectSite,
  draggable = false,
  onDragEnd,
  behindOverlaysWhen = false,
  suspendFitRef,
  ensurePanes = true,
  panes = { normal: 'markerPane', behindOverlays: 'sitePane' },
}: {
  mode: 'many-dots' | 'single-pin';
  markerStyle?: 'dot' | 'pin';
  sites: SitePlacement[];
  selectedSiteId: string | null;
  onSelectSite?: (siteId: string | null) => void;
  draggable?: boolean;
  onDragEnd?: (siteId: string, lat: number, lng: number) => void;
  behindOverlaysWhen?: boolean;
  suspendFitRef?: MutableRefObject<boolean>;
  ensurePanes?: boolean;
  panes?: PaneConfig;
}) {
  const map = useMap();
  const markersRef = useRef<Record<string, L.Marker>>({});
  const sitesRef = useRef(sites);
  sitesRef.current = sites;
  const handlersRef = useRef({ onSelectSite, onDragEnd, suspendFitRef });
  handlersRef.current = { onSelectSite, onDragEnd, suspendFitRef };

  const sitesSig = useMemo(
    () =>
      JSON.stringify(
        sites.map((s) => ({
          id: s.id,
          lat: s.latitude,
          lng: s.longitude,
          name: s.name,
        })),
      ),
    [sites],
  );

  useLayoutEffect(() => {
    if (!ensurePanes) return;
    const existing = map.getPane(panes.behindOverlays);
    const p = existing ?? map.createPane(panes.behindOverlays);
    if (p) p.style.zIndex = '420';
  }, [map, ensurePanes, panes.behindOverlays]);

  useEffect(() => {
    const placed = sitesRef.current;
    const nextIds = new Set(placed.map((s) => s.id));
    for (const [id, m] of Object.entries(markersRef.current)) {
      if (!nextIds.has(id)) {
        map.removeLayer(m);
        delete markersRef.current[id];
      }
    }

    placed.forEach((s) => {
      const selected = s.id === selectedSiteId;
      const paneName = behindOverlaysWhen ? panes.behindOverlays : panes.normal;
      const existing = markersRef.current[s.id];

      if (!existing) {
        const icon =
          markerStyle === 'pin'
            ? SITE_PIN_ICON
            : mode === 'many-dots'
              ? dotIcon(selected)
              : dotIcon(true);
        const m = L.marker([Number(s.latitude), Number(s.longitude)], {
          icon,
          draggable: mode === 'single-pin' ? draggable : false,
          pane: paneName,
          zIndexOffset:
            mode === 'single-pin'
              ? draggable
                ? behindOverlaysWhen
                  ? 300
                  : 1200
                : 0
              : selected
                ? 800
                : 400,
          bubblingMouseEvents: false,
        }).addTo(map);

        m.bindTooltip(s.name, {
          direction: 'top',
          offset: mode === 'single-pin' ? [0, -24] : [0, -12],
          opacity: 0.95,
        });

        if (handlersRef.current.onSelectSite && mode === 'many-dots') {
          m.on('click', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            handlersRef.current.onSelectSite?.(s.id);
          });
        }

        if (mode === 'single-pin' && draggable && handlersRef.current.onDragEnd) {
          m.on('dragstart', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            const suspend = handlersRef.current.suspendFitRef;
            if (suspend) suspend.current = true;
          });
          m.on('dragend', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            const suspend = handlersRef.current.suspendFitRef;
            if (suspend) suspend.current = false;
            const mm = e.target as L.Marker;
            const ll = mm.getLatLng();
            handlersRef.current.onDragEnd?.(s.id, ll.lat, ll.lng);
          });
        }

        markersRef.current[s.id] = m;
      } else {
        existing.setLatLng([Number(s.latitude), Number(s.longitude)]);
        existing.setZIndexOffset(
          mode === 'single-pin'
            ? draggable
              ? behindOverlaysWhen
                ? 300
                : 1200
              : 0
            : selected
              ? 800
              : 400,
        );

        if (markerStyle === 'dot') {
          if (mode === 'many-dots') existing.setIcon(dotIcon(selected));
          if (mode === 'single-pin') existing.setIcon(dotIcon(true));
        }

        if (mode === 'single-pin') {
          const shouldDrag = Boolean(draggable);
          if (shouldDrag && !existing.dragging?.enabled()) existing.dragging?.enable();
          if (!shouldDrag && existing.dragging?.enabled()) existing.dragging?.disable();
        }

        const curPane = (existing.options as { pane?: string }).pane;
        if (curPane !== paneName) {
          map.removeLayer(existing);
          (existing.options as { pane?: string }).pane = paneName;
          existing.addTo(map);
        }
      }
    });

    return () => {
      for (const m of Object.values(markersRef.current)) map.removeLayer(m);
      markersRef.current = {};
    };
  }, [map, sitesSig, selectedSiteId, mode, markerStyle, draggable, behindOverlaysWhen, panes.behindOverlays, panes.normal]);

  return null;
}
